import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { Typography, Radio, Input, DatePicker } from '@chilecompra/react-kit/components'
import {
  checkInputErrorDni,
  checkInputErrorMaxLength,
  checkInputErrorRegEx,
  checkInputErrorRequired,
  useDatePicker,
  useInput,
  useRadio
} from '@chilecompra/react-kit'

import { onGetSignerInfoDNIThunk } from './DirectDealStepZero.actions'

import { SearchSuffix, SearchIconCustom, GridItem, GridContainer } from './DirectDealStepZero.styles'

import { formatDNI, unformatDNI } from '../../modules/utils/formatters'
import { ONLY_LETTERS } from '../../modules/utils/regexes'
import { useImperativeFormRef } from '../../modules/hooks/useFormRef'
import {
  DIRECT_DEAL_STEP_ZERO_OPTIONS,
  DIRECT_DEAL_DECLARATION_ORIGIN_DATE,
  DIRECT_DEAL_DECLARATION_OLD_ORIGIN_DATE,
  NOTIFICATIONS_ORIGIN_SELECTOR_CODE
} from '../../config/settings/constants'

/**
 * The DirectDealOrigin's
 */
const DirectDealOrigin = props => {
  const { error, loading, formRef, values } = props

  const dispatch = useDispatch()
  const { notifications } = useSelector(state => state.notifications)

  const [hasError, setHasError] = useState(false)
  const [maxDate, setMaxDate] = useState(null)

  const showNewVersion = useMemo(() => {
    const config = notifications.find(item => item.codigo === NOTIFICATIONS_ORIGIN_SELECTOR_CODE)
    if (!config) return false
    const serverTime = new Date(config.horaServidor)
    const inicioDate = new Date(config.inicio)
    return serverTime >= inicioDate
  }, [notifications])

  const [minDate, setMinDate] = useState(showNewVersion ? new Date(DIRECT_DEAL_DECLARATION_ORIGIN_DATE) : null)

  const {
    error: resolutionNumberError,
    onError: handleResolutionNumberError,
    onChange: handleResolutionNumberChange,
    setValue: setResolutionNumberValue,
    value: resolutionNumberValue
  } = useInput({
    errorCallbacks: [checkInputErrorMaxLength(99), checkInputErrorRequired()],
    initialValue: values?.resolution || ''
  })

  const {
    error: endDateExecuteError,
    onChange: handleEndDateExecuteChange,
    onError: handleEndDateExecuteError,
    setValue: setEndDateExecuteValue,
    value: endDateExecuteValue
  } = useDatePicker({
    errorCallbacks: [checkInputErrorRequired()],
    initialValue: values?.resolutionDate || new Date()
  })

  const {
    error: signerError,
    onChange: handleSignerChange,
    onError: handleSignerError,
    setValue: setSignerValue,
    value: signerValue
  } = useInput({
    changeCallback: () => {
      setNameValue('')
      setLastNameValue('')

      if (hasError) {
        setHasError(false)
      }
    },
    errorCallbacks: [checkInputErrorRequired(), checkInputErrorDni()],
    formatCallbacks: [value => formatDNI(value)],
    initialValue: values?.dni || ''
  })

  const {
    error: nameError,
    onChange: handleNameChange,
    onError: handleNameError,
    onReset: handleNameReset,
    setValue: setNameValue,
    value: nameValue
  } = useInput({
    errorCallbacks: [checkInputErrorRequired(), checkInputErrorRegEx(ONLY_LETTERS, 'Este campo solo permite letras')],
    formatCallbacks: [value => value?.toUpperCase()],
    initialValue: values?.names || ''
  })

  const {
    error: lastNameError,
    onChange: handleLastNameChange,
    onError: handleLastNameError,
    onReset: handleLastNameReset,
    value: lastNameValue,
    setValue: setLastNameValue
  } = useInput({
    errorCallbacks: [checkInputErrorRequired(), checkInputErrorRegEx(ONLY_LETTERS, 'Este campo solo permite letras')],
    formatCallbacks: [value => value?.toUpperCase()],
    initialValue: values?.lastNames || ''
  })

  const {
    error: chargeError,
    onChange: handleChargeChange,
    onError: handleChargeError,
    setValue: setChargeValue,
    value: chargeValue
  } = useInput({
    initialValue: values?.job || '',
    errorCallbacks: [checkInputErrorRequired()]
  })

  const {
    value: originValue,
    onChange: handleOriginChange,
    setValue: setOriginValue
  } = useRadio({
    errorCallbacks: [checkInputErrorRequired()],
    initialValue: values?.origin || DIRECT_DEAL_STEP_ZERO_OPTIONS[1].value,
    changeCallback: updatedValue => {
      if (showNewVersion) return // Don't change dates in new version

      if (updatedValue === DIRECT_DEAL_STEP_ZERO_OPTIONS[0].value) {
        setEndDateExecuteValue(new Date(DIRECT_DEAL_DECLARATION_OLD_ORIGIN_DATE))
        setMaxDate(new Date(DIRECT_DEAL_DECLARATION_OLD_ORIGIN_DATE))
        setMinDate(null)
      } else if (updatedValue === DIRECT_DEAL_STEP_ZERO_OPTIONS[1].value) {
        setEndDateExecuteValue(new Date(DIRECT_DEAL_DECLARATION_ORIGIN_DATE))
        setMinDate(new Date(DIRECT_DEAL_DECLARATION_ORIGIN_DATE))
        setMaxDate(null)
      }
    }
  })

  const handlerSearchDni = () => {
    dispatch(
      onGetSignerInfoDNIThunk({
        dni: unformatDNI(signerValue),
        callback: signer => {
          handleNameReset()
          handleLastNameReset()

          setNameValue(signer?.names || '')
          setLastNameValue(signer?.lastNames || values?.lastNames || '')
        }
      })
    )
  }

  useImperativeFormRef(
    formRef,
    () => ({
      resolution: {
        error: resolutionNumberError,
        onError: handleResolutionNumberError,
        value: resolutionNumberValue
      },
      dateExecute: {
        error: endDateExecuteError,
        onError: handleEndDateExecuteError,
        value: endDateExecuteValue
      },
      signer: {
        error: signerError,
        onError: handleSignerError,
        value: signerValue
      },
      name: {
        error: nameError,
        onError: handleNameError,
        value: nameValue
      },
      lastName: {
        error: lastNameError,
        onError: handleLastNameError,
        value: lastNameValue
      },
      charge: {
        error: chargeError,
        onError: handleChargeError,
        value: chargeValue
      },
      origin: {
        value: showNewVersion ? DIRECT_DEAL_STEP_ZERO_OPTIONS[1].value : originValue
      }
    }),
    [
      resolutionNumberValue,
      resolutionNumberError,
      endDateExecuteError,
      endDateExecuteError,
      signerValue,
      signerError,
      nameError,
      nameValue,
      lastNameError,
      lastNameValue,
      chargeValue,
      chargeError,
      originValue,
      showNewVersion
    ]
  )

  useEffect(() => {
    setResolutionNumberValue(values?.resolution || '')
    setEndDateExecuteValue(values?.resolutionDate || new Date())
    setSignerValue(values?.dni || '')
    setNameValue(values?.names || '')
    setLastNameValue(values?.lastNames || '')
    setChargeValue(values?.job || '')
    if (!showNewVersion) {
      setOriginValue(values?.origin || DIRECT_DEAL_STEP_ZERO_OPTIONS[1].value)
    }
  }, [
    values,
    setResolutionNumberValue,
    setEndDateExecuteValue,
    setSignerValue,
    setNameValue,
    setLastNameValue,
    setChargeValue,
    setOriginValue,
    showNewVersion
  ])

  useEffect(() => {
    if (showNewVersion) {
      setMinDate(new Date(DIRECT_DEAL_DECLARATION_ORIGIN_DATE))
      setMaxDate(null)
    }
  }, [showNewVersion])

  useEffect(() => {
    setHasError(error?.reason === 'CIVIL_REGISTRY_SERVICE_UNAVAILABLE')
  }, [error])

  return (
    <GridContainer margin="33px 0" spacing={2}>
      <GridItem lg={2} md={2} sm={12} xs={12}>
        {!showNewVersion && (
          <>
            <Typography fontWeight="bold" variant="body2" fontSize="16px" padding="0 0 16px" tabIndex="0">
              Origen del Trato Directo
            </Typography>
            <Typography fontSize="14px" fontWeight="regular" lineHeight="18px" variant="body2" tabIndex="0">
              Indícanos si este nuevo Trato Directo proviene de una resolución o decreto que haya sido firmado antes de
              la entrada en vigencia de la nueva ley de compras públicas N°19.886.
            </Typography>
          </>
        )}
        {showNewVersion && (
          <GridContainer spacing={1}>
            <GridItem lg={12} md={12} sm={12} xs={12}>
              <Typography fontWeight="bold" variant="body2" fontSize="16px" padding="10px 0 5px" tabIndex="0">
                Documento que aprueba el trato directo o intención
              </Typography>
              <Typography variant="body2" tabIndex="0">
                Ingresa los datos, pordrás cargar el archivo en el paso siguiente.
              </Typography>
            </GridItem>
            <GridItem lg={12} md={12} sm={12} xs={12} style={{ height: '25px' }} />
            <GridItem lg={12} md={12} sm={12} xs={12}>
              <Typography fontWeight="bold" variant="body2" fontSize="16px" padding="32px 0 5px" tabIndex="0">
                Responsable de la contratación
              </Typography>
              <Typography variant="body2" tabIndex="0">
                El responsable de la contratación será la persona encargada de supervisar y garantizar la correcta
                ejecución del Trato Directo.
              </Typography>
            </GridItem>
          </GridContainer>
        )}
      </GridItem>

      <GridItem lg={6} md={6} sm={12} xs={12}>
        {!showNewVersion && (
          <>
            <Typography fontWeight="bold" variant="body2" fontSize="16px" padding="0 8px 16px" tabIndex="0">
              ¿El Trato Directo proviene de una resolución firmada antes del día 12 de diciembre de 2024?
            </Typography>
            <Radio onChange={handleOriginChange} options={DIRECT_DEAL_STEP_ZERO_OPTIONS} value={originValue} />
          </>
        )}
        <GridContainer
          spacing={1}
          margin={showNewVersion ? '0' : '10px 0 0'}
          padding={showNewVersion ? '0 14px 0 0' : '16px 14px 0 0'}
        >
          <GridItem lg={12} md={12} sm={12} xs={12}>
            <Typography fontWeight="bold" variant="body2" fontSize="16px" padding="0 0 16px" tabIndex="0">
              Acto administrativo o documento de intención de Trato Directo
            </Typography>
          </GridItem>
          <GridItem lg={6} md={6} sm={6} xs={12}>
            <Input
              error={resolutionNumberError}
              label="Número"
              maxLength={100}
              onChange={handleResolutionNumberChange}
              onError={handleResolutionNumberError}
              value={resolutionNumberValue}
            />
          </GridItem>
          <GridItem lg={6} md={6} sm={6} xs={12}>
            <DatePicker
              width="100%"
              maxDate={maxDate}
              minDate={minDate}
              error={endDateExecuteError}
              format="dd/MM/yyyy"
              label="Fecha"
              placeholder="Fecha"
              value={endDateExecuteValue}
              onChange={handleEndDateExecuteChange}
            />
          </GridItem>
          <GridItem lg={12} md={12} sm={12} xs={12}>
            <Typography fontWeight="bold" variant="body2" fontSize="16px" padding="32px 0 16px" tabIndex="0">
              Responsable de la contratación
            </Typography>
          </GridItem>
          <GridItem lg={6} md={6} sm={6} xs={12}>
            <Input
              label="Rut responsable"
              value={signerValue}
              onChange={handleSignerChange}
              onError={handleSignerError}
              error={signerError}
              suffix={
                <SearchSuffix aria-label="Buscar RUT" onClick={handlerSearchDni}>
                  <SearchIconCustom />
                </SearchSuffix>
              }
            />
          </GridItem>
          <GridItem md={6} sm={6} />
          <GridItem lg={6} md={6} sm={6} xs={12}>
            <Input
              disabled={!hasError}
              error={nameError}
              label="Nombre"
              loading={loading}
              onChange={handleNameChange}
              onError={handleNameError}
              value={nameValue}
            />
          </GridItem>
          <GridItem lg={6} md={6} sm={6} xs={12}>
            <Input
              disabled={!hasError}
              error={lastNameError}
              label="Apellido"
              loading={loading}
              onChange={handleLastNameChange}
              onError={handleLastNameError}
              value={lastNameValue}
            />
          </GridItem>
          <GridItem lg={12} md={12} sm={12} xs={12}>
            <Input
              label="Cargo"
              value={chargeValue}
              onChange={handleChargeChange}
              onError={handleChargeError}
              error={chargeError}
            />
          </GridItem>
        </GridContainer>
      </GridItem>
      <GridItem lg={4} md={4} sm={12} xs={12} />
    </GridContainer>
  )
}

DirectDealOrigin.propTypes = {
  error: PropTypes.shape({
    reason: PropTypes.string
  }),
  loading: PropTypes.bool,
  formRef: PropTypes.shape({
    current: PropTypes.shape(Object)
  }),
  values: PropTypes.shape({
    resolution: PropTypes.string,
    resolutionDate: PropTypes.string,
    names: PropTypes.string,
    lastNames: PropTypes.string,
    dni: PropTypes.string,
    job: PropTypes.string,
    origin: PropTypes.string
  })
}

export default DirectDealOrigin
